import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from './auth.service';
import { catchError, switchMap, throwError } from 'rxjs';
import { NzModalService } from 'ng-zorro-antd/modal';
import { Router } from '@angular/router';

let isSessionExpiredModalOpen = false;

/** Renewal endpoints must never be retried through themselves. */
const AUTH_ENDPOINTS = ['/users/login', '/users/refresh', '/users/logout'];

const isAuthEndpoint = (url: string): boolean =>
  AUTH_ENDPOINTS.some((endpoint) => url.includes(endpoint));

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const modalService = inject(NzModalService);
  const router = inject(Router);
  const token = authService.token;

  const withAuth = (accessToken: string | null) =>
    accessToken
      ? req.clone({ setHeaders: { Authorization: `Bearer ${accessToken}` } })
      : req;

  return next(withAuth(token)).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status !== 401 || isAuthEndpoint(req.url)) {
        return throwError(() => error);
      }

      // A 401 with a refresh token in hand simply means the access token aged out.
      // Renew once and replay the request, invisibly to the user.
      if (authService.refreshToken) {
        return authService.refreshSession().pipe(
          switchMap((accessToken) => next(withAuth(accessToken))),
          catchError(() => {
            promptReconnect(modalService, router, authService);
            return throwError(() => error);
          })
        );
      }

      promptReconnect(modalService, router, authService);
      return throwError(() => error);
    })
  );
};

function promptReconnect(
  modalService: NzModalService,
  router: Router,
  authService: AuthService
): void {
  if (isSessionExpiredModalOpen) {
    return;
  }

  isSessionExpiredModalOpen = true;
  authService.logout();
  modalService.error({
    nzTitle: 'Session expirée',
    nzContent:
      'Votre session a expiré. Veuillez vous reconnecter pour continuer.',
    nzOkText: 'Se reconnecter',
    nzOnOk: () => {
      isSessionExpiredModalOpen = false;
      void router.navigate(['/login'], {
        queryParams: { returnUrl: router.url },
      });
    },
  });
}
